/**
 * Vendor edit — fed from `$vendorId.index`'s "Edit" CTA.
 *
 * Uses the same GSTIN-first composer as new vendor, hydrated from
 * `vendors.get`. Bank-detail changes remain a separate founder step-up
 * flow; this page posts master-field diffs only.
 *
 * @see prototype/purchases/vendor-new.html — V1.1 vendor composer baseline
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Alert } from "@flint/ui";
import { trpc } from "../../../../lib/trpc.ts";
import {
  formFromVendor,
  VendorForm,
  valuesToUpdatePayload,
} from "../../../../features/vendors/index.ts";
import { WorkspaceShell } from "../../../../features/workspace/index.ts";

export const Route = createFileRoute("/(workspace)/procurement/vendors/$vendorId/edit")({
  component: EditVendorPage,
});

function EditVendorPage() {
  const { vendorId } = Route.useParams();
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const vendor = trpc.vendors.get.useQuery({ id: vendorId });

  const update = trpc.vendors.update.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.vendors.list.invalidate(),
        utils.vendors.get.invalidate({ id: vendorId }),
      ]);
    },
  });

  const baseCrumbs = [
    { label: "Procurement", to: "/" },
    { label: "Vendors", to: "/procurement/vendors" },
  ];

  if (vendor.isLoading) {
    return (
      <WorkspaceShell breadcrumbs={[...baseCrumbs, { label: "Edit" }]}>
        <p className="text-muted">Loading vendor…</p>
      </WorkspaceShell>
    );
  }
  if (vendor.error) {
    return (
      <WorkspaceShell breadcrumbs={[...baseCrumbs, { label: "Edit" }]}>
        <Alert intent="bad" title="Couldn't load vendor" description={vendor.error.message} />
      </WorkspaceShell>
    );
  }
  if (!vendor.data) return null;

  const baseline = formFromVendor(vendor.data);

  return (
    <WorkspaceShell
      breadcrumbs={[
        ...baseCrumbs,
        { label: vendor.data.name, to: "/procurement/vendors/$vendorId" },
        { label: "Edit" },
      ]}
      contentClassName="max-w-none h-full"
      viewportClassName="p-0"
      disableViewportScroll
    >
      <VendorForm
        defaultValues={baseline}
        submitLabel="Save changes"
        pendingLabel="Saving…"
        pending={update.isPending}
        serverError={update.error?.message ?? null}
        mode="edit"
        backHref={`/procurement/vendors/${vendorId}`}
        recordHref={`/procurement/vendors/${vendorId}`}
        onSubmit={async (values, { next }) => {
          const patch = valuesToUpdatePayload(values, baseline, vendorId);
          const changedKeys = Object.keys(patch).filter((k) => k !== "id");
          if (changedKeys.length > 0) {
            await update.mutateAsync(patch);
          }
          // Edit mode supports two intents:
          //   open → back to the vendor's detail page (default)
          //   list → back to the vendors list
          // 'add-another' is hidden in edit mode (no "add" semantic);
          // VendorForm's footer doesn't render that menu item here.
          if (next === "list") {
            void navigate({ to: "/procurement/vendors" });
            return;
          }
          void navigate({
            to: "/procurement/vendors/$vendorId",
            params: { vendorId },
          });
        }}
      />
    </WorkspaceShell>
  );
}
